import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@zendeskgarden/react-loaders";
import { ArrowIcon } from "../../../assets/Icons";
import {
  AccordionContainer,
  AccordionTitle,
  ArrowWrapper,
  SubAccordionTitle,
  GridContainer,
  GridRow,
  GridLabel,
  GridValue,
  AccordionButtonContainer,
} from "./style";
import type { ZenAccordionProps } from "./interface";
import type { ReactNode } from "react";

function TruncatedValue({ value }: { value: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value]);

  return (
    <GridValue
      ref={ref}
      title={overflows && typeof value === "string" ? value : undefined}
      $overflows={overflows}
    >
      {value}
    </GridValue>
  );
}

export function ZenAccordion({
  title,
  labels = [],
  values = [],
  subAccordions,
  isLoading = false,
  button,
}: ZenAccordionProps) {
  const [open, setOpen] = useState(true);
  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({});

  function toggleSub(subTitle: string) {
    setOpenSubs((prev) => ({ ...prev, [subTitle]: !prev[subTitle] }));
  }

  return (
    <AccordionContainer>
      <AccordionTitle onClick={() => setOpen((v) => !v)}>
        {title}
        <ArrowWrapper $open={open}>
          <ArrowIcon />
        </ArrowWrapper>
      </AccordionTitle>

      {open && (
        <GridContainer>
          {isLoading
            ? (labels.length > 0 ? labels : Array.from({ length: 3 }, (_, i) => String(i))).map((label, index) => (
                <GridRow key={index} $index={index}>
                  <GridLabel>{typeof label === 'string' && label !== String(index) ? label : <Skeleton width="50%" />}</GridLabel>
                  <GridValue><Skeleton width="80%" /></GridValue>
                </GridRow>
              ))
            : labels.map((label, index) => (
                <GridRow key={label} $index={index}>
                  <GridLabel>{label}</GridLabel>
                  <TruncatedValue value={values[index]} />
                </GridRow>
              ))}
              {button && (
                <AccordionButtonContainer>
                  <GridRow $index={0}>
                    <span>{button.label}</span>

                    <button onClick={button.link}>Clique aqui</button>
                  </GridRow>
                </AccordionButtonContainer>
              )}
        </GridContainer>
      )}

      {subAccordions?.map((sub) => (
        <div key={sub.title}>
          <SubAccordionTitle onClick={() => toggleSub(sub.title)}>
            {sub.title}
            <ArrowWrapper $open={!!openSubs[sub.title]}>
              <ArrowIcon />
            </ArrowWrapper>
          </SubAccordionTitle>
          {openSubs[sub.title] && (
            <>
              <GridContainer>
                {sub.labels.map((label, index) => (
                  <GridRow key={label} $index={index}>
                    <GridLabel>{label}</GridLabel>
                    <TruncatedValue value={sub.values[index]} />
                  </GridRow>
                ))}
              </GridContainer>
              {sub.button && (
                <AccordionButtonContainer>
                  <GridRow $index={0}>
                    <span>{sub.button.label}</span>

                    <button onClick={sub.button.link}>Clique aqui</button>
                  </GridRow>
                </AccordionButtonContainer>
              )}
            </>
          )}
        </div>
      ))}
    </AccordionContainer>
  );
}
